import {
  baseContractCandidateOf,
  baseContractIneligibility,
  type BaseContractType,
  type ContractDetail,
  type DecideContractTypeResponse,
  type NominateBaseContractResponse,
} from '@clm/shared-types';

import { nominateBaseContract } from '../api/base-contracts';
import { decideContractType } from '../api/contract-type-proposals';
import { nominationProblemOf, type NominationProblem } from './base-msa-shared';
import { decisionProblemOf, type DecisionProblem } from './contract-type-decision';

/**
 * **Confirm type and set as base** — CLM-118 (R-118-13, B26). Shared by the
 * Base MSA page and the contract record's "Set as base MSA".
 *
 * A base MSA's type is **administrator-confirmed**: the server's invariant is
 * unchanged, so an MSA confirmed because the file name and the AI agree
 * (`auto_confirmed`) is refused as `type_not_admin_confirmed`. Nominating it
 * here records the Admin's confirmation first, then the nomination — two
 * audited acts, each by its own route (`decide-contract-type`, posting the
 * record's own `seen` unchanged, then `nominate-base-contract`). Nothing is
 * folded into the nominate route, whose receipt check counts exactly the rows
 * the nomination writes.
 *
 * If the nomination fails after the confirmation, the type stays confirmed by
 * that Admin — harmless, and the outcome says so.
 */

/**
 * Whether the contract is offered **Confirm type and set as base**: settled by
 * the file name and the AI with no administrator, decidable (the API built its
 * `seen`), and eligible once an administrator confirms the type.
 */
export function confirmsTypeFirst(type: BaseContractType, contract: ContractDetail): boolean {
  const candidate = baseContractCandidateOf(contract);
  return (
    baseContractIneligibility(type, candidate) === 'type_not_admin_confirmed' &&
    baseContractIneligibility(type, { ...candidate, contract_type_confirmation: 'confirmed' }) === null &&
    contract.contract_type_status.seen !== null
  );
}

export type ConfirmAndSetOutcome =
  | {
      readonly kind: 'done';
      readonly decision: DecideContractTypeResponse;
      readonly nomination: NominateBaseContractResponse;
    }
  /** Nothing was written: the decision was refused, so no nomination was attempted. */
  | { readonly kind: 'decision_failed'; readonly problem: DecisionProblem }
  /** The type is confirmed by this Admin; the nomination was refused. */
  | {
      readonly kind: 'nomination_failed';
      readonly decision: DecideContractTypeResponse;
      readonly problem: NominationProblem;
    };

export async function confirmTypeAndSetBase(input: {
  readonly type: BaseContractType;
  readonly contract: ContractDetail;
  readonly expected_current_nomination_id: string | null;
  readonly note?: string;
  /** A practices scope with no visible base: `base_changed` means something different there. */
  readonly scopedWithoutBase: boolean;
}): Promise<ConfirmAndSetOutcome> {
  const seen = input.contract.contract_type_status.seen;
  if (seen === null) throw new Error('confirmTypeAndSetBase needs a decidable contract');
  let decision: DecideContractTypeResponse;
  try {
    decision = await decideContractType({ contract_key: input.contract.contract_key, contract_type: input.type, seen });
  } catch (error) {
    return { kind: 'decision_failed', problem: decisionProblemOf(error) };
  }
  try {
    const nomination = await nominateBaseContract({
      contract_type: input.type,
      contract_key: input.contract.contract_key,
      expected_current_nomination_id: input.expected_current_nomination_id,
      ...(input.note === undefined || input.note === '' ? {} : { note: input.note }),
    });
    return { kind: 'done', decision, nomination };
  } catch (error) {
    return {
      kind: 'nomination_failed',
      decision,
      problem: nominationProblemOf(error, { scopedWithoutBase: input.scopedWithoutBase }),
    };
  }
}
